import { EyeOff, Plus, UserPlus } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Avatar } from '@/components/ui/Avatar'

import { SuppressedValue } from '@/components/data/SuppressedValue'
import { EmptyState } from '@/components/feedback/EmptyState'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Tag } from '@/components/ui/Tag'
import { cn } from '@/lib/cn'
import {
  toParticipantPayload,
  useAddParticipant,
  useRemoveParticipant,
  useUpdateParticipant,
} from '@/features/cases/api/useCase'
import { CaseDocuments } from '@/features/cases/components/CaseDocuments'
import { CaseFiles } from '@/features/cases/components/CaseFiles'
import { CaseHistory } from '@/features/cases/components/CaseHistory'
import { CaseParticipantFormSheet } from '@/features/cases/components/CaseRecordSheets'
import { refusalMessage } from '@/features/cases/lib/refusal'
import { isSuppressed } from '@/types/domain'
import type { CaseDetail, CaseParticipant } from '@/types/domain'

/** `null` closed, `'new'` adding, a record editing — see DirectoriesPage. */
type EditTarget<T> = T | 'new' | null

function editing<T>(target: EditTarget<T>): T | undefined {
  return target === null || target === 'new' ? undefined : target
}

/* ── Overview ─────────────────────────────────────────────────────────────── */

/** The route with the shipment's position on it, the declared cargo against
 *  what the border scale said, the boxes and the deal terms — FR-24 read back
 *  as four cards, one question each.
 *
 *  A re-export rather than a pass-through component, for the reason the Legs
 *  and Money sections state below: this is not a slice of the composed case
 *  detail rendered by a wrapper, it is four hundred lines of route strip, cargo
 *  ledger, container list and fact grid that now live in `./overview` split
 *  across the files they belong in. The call site in CaseDetailPage — and the
 *  props it passes — are unchanged. */
export { OverviewSection } from './overview'

/* ── Legs ─────────────────────────────────────────────────────────────────── */

/** The legs the shipment is actually moving on, one card each, with the sheet
 *  that adds, edits and removes them (FR-24, FR-26, FR-52, FR-67).
 *
 *  A re-export rather than a pass-through component, for the reason the Money
 *  section states below: this is not a slice of the composed case detail
 *  rendered by a wrapper, it is three hundred lines of card, flagged place,
 *  fact grid, container strip and mutation wiring that now live in `./legs`
 *  split across the files they belong in. The call site in CaseDetailPage —
 *  and the props it passes — are unchanged. */
export { LegsSection } from './legs'

/* ── Participants ─────────────────────────────────────────────────────────── */

/* Takes the whole record rather than just the array, unlike the shape this
   section had while it was read-only: a write needs the case id, and reading it
   off the record is what stops a caller passing a list from one case and an id
   from another. */
export function ParticipantsSection({ record }: { record: CaseDetail }) {
  const { t } = useTranslation()
  const [target, setTarget] = useState<EditTarget<CaseParticipant>>(null)
  const participants = record.participants

  const addParticipant = useAddParticipant(record.id)
  const updateParticipant = useUpdateParticipant(record.id)
  const removeParticipant = useRemoveParticipant(record.id)

  /* The sheet stays OPEN while the save is in flight and stays open holding
     the draft if the server refuses, so nothing typed is lost to a failed save
     (UX-DR-18). It closes only once the server has answered. */
  const editingParticipant = editing(target)
  const pending =
    addParticipant.isPending ||
    updateParticipant.isPending ||
    removeParticipant.isPending
  const failure =
    addParticipant.error ?? updateParticipant.error ?? removeParticipant.error

  const sheet = (
    <CaseParticipantFormSheet
      open={target !== null}
      onClose={() => setTarget(null)}
      record={editingParticipant}
      pending={pending}
      error={failure ? refusalMessage(failure, t) : undefined}
      onSubmit={(values) => {
        const payload = toParticipantPayload(values)
        const done = { onSuccess: () => setTarget(null) }
        if (editingParticipant) {
          updateParticipant.mutate(
            { participantId: editingParticipant.id, payload },
            done,
          )
        } else {
          addParticipant.mutate(payload, done)
        }
      }}
      /* The case's participants section wires delete now. It used to be the one
         half of a deliberate asymmetry with the quote's — the quote's wired
         delete and showed mutation errors, the case's did neither — and the
         asymmetry was never a decision about cases, only about which surface
         got there first. */
      onDelete={
        editingParticipant
          ? () =>
              removeParticipant.mutate(editingParticipant.id, {
                onSuccess: () => setTarget(null),
              })
          : undefined
      }
    />
  )

  if (participants.length === 0) {
    return (
      <Card>
        <EmptyState
          title={t('cases.participants.emptyTitle')}
          description={t('cases.participants.emptyDescription')}
          action={
            <Button size="sm" iconLeft={Plus} onClick={() => setTarget('new')}>
              {t('cases.participants.add')}
            </Button>
          }
        />
        {sheet}
      </Card>
    )
  }

  return (
    <div className="flex flex-col gap-3.5">
      {/* The section's one line of chrome: the count and the delivery rule
          ("external parties are reached by email only") as quiet prose, with
          the add action at the far edge — no card header, because the cards
          below are the content and need no title above them. */}
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
        <span className="text-text-muted text-[13px] leading-snug font-light">
          {t('cases.participants.count', { count: participants.length })} ·{' '}
          {t('cases.participants.externalNote')}
        </span>
        <Button
          variant="secondary"
          size="sm"
          iconLeft={UserPlus}
          onClick={() => setTarget('new')}
        >
          {t('cases.participants.add')}
        </Button>
      </div>

      {/* One card per participant, two abreast on desktop: each party reads as
          its own tile — who they are, how they are reached, what they are on
          the case — rather than a row in a register. */}
      <ul className="grid gap-3 lg:grid-cols-2">
        {participants.map((participant) => (
          <li
            key={participant.id}
            onClick={() => setTarget(participant)}
            tabIndex={0}
            aria-label={t('cases.participants.edit')}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                setTarget(participant)
              }
            }}
            className={cn(
              'bg-surface border-border-subtle rounded-[var(--radius-DEFAULT)] border shadow-xs',
              'hover:bg-surface-hover flex cursor-pointer items-center gap-3 px-4 py-3.5 transition-colors',
            )}
          >
            {/* A suppressed name gets a faceless circle, not initials — initials
                derived from a hidden name would leak the very thing FR-29 hides. */}
            {isSuppressed(participant.name) ? (
              <span
                aria-hidden
                className="bg-neutral-100 text-text-muted flex size-8 shrink-0 items-center justify-center rounded-full"
              >
                <EyeOff size={13} />
              </span>
            ) : (
              <Avatar name={participant.name} size="md" />
            )}

            <span className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="text-text-heading text-[13px] leading-tight font-medium">
                {/* An agent-typed slot reads "Agent hidden" for Sales rather than
                    appearing empty, so missing data is never mistaken for
                    unentered data (FR-29, AD-6). */}
                <SuppressedValue value={participant.name} />
              </span>
              <span className="text-text-muted text-[12px] leading-snug font-light">
                <SuppressedValue
                  value={participant.contact}
                  label={t('common.suppressed.contact')}
                  fallback={t('cases.participants.noContact')}
                />
                {/* The notification fact rides the contact line as prose, the
                    way the mock reads — the right edge stays reserved for the
                    one tag that types the card. */}
                {participant.notifyByEmail
                  ? ` · ${t('cases.participants.notifiedByEmail')}`
                  : null}
              </span>
            </span>

            {/* Free width: a translated role must never be truncated to fit a
                width measured in English (UX-DR-30). */}
            <Tag>{t(`participantRole.${participant.role}`)}</Tag>
          </li>
        ))}
      </ul>

      {sheet}
    </div>
  )
}

/* ── Money ────────────────────────────────────────────────────────────────── */

/** The plan against the fact, the balances that fall out of it, and the
 *  receivable series (UX-DR-17, UX-DR-18).
 *
 *  A re-export rather than a pass-through component, and the difference is the
 *  point: this section is not a slice of the composed case detail rendered by a
 *  wrapper, it is five hundred lines of table, balance cells, profit cells,
 *  tranche strip and sheet wiring that now live in `./money` split across the
 *  files they belong in. The call site in CaseDetailPage — and the props it
 *  passes — are unchanged, which is what let the split happen without touching
 *  the page's composition. */
export { MoneySection } from './money'

/* ── Documents ────────────────────────────────────────────────────────────── */

/** The case's invoices and debit notes (FR-45).
 *
 *  A pass-through for the same reason FilesSection is one: the paper is not a
 *  slice of the composed case detail. It lives in the finance archive, has its
 *  own query and its own invalidation, and a document raised here has to move
 *  the Money page too. The call site in CaseDetailPage stays uniform with the
 *  sections either side of it. */
export function DocumentsSection({ record }: { record: CaseDetail }) {
  return <CaseDocuments record={record} />
}

/* ── Files ────────────────────────────────────────────────────────────────── */

/** The file area, bound to its own endpoints (Story 8.1).
 *
 *  A pass-through rather than a component, and deliberately so. Every other
 *  section here renders a slice of the composed case detail, but files came off
 *  that payload when they became real: they have their own query, their own
 *  invalidation after an upload, and a lifetime that is not the detail's. The
 *  call site in CaseDetailPage stays identical, which is what let the binding
 *  happen without touching the page's composition.
 */
export function FilesSection({ caseId }: { caseId: string }) {
  return <CaseFiles caseId={caseId} />
}

/* ── History ──────────────────────────────────────────────────────────────── */

/** Everything that has happened to this case (FR-37, UX-DR-12).
 *
 *  A pass-through, for the same reason FilesSection and DocumentsSection are
 *  ones: the case's past is not a slice of the composed detail. It is TWO reads
 *  — the action log and the financial audit trail — with their own queries,
 *  their own error states and a lifetime that is not the detail's, merged into
 *  one feed by the component below. It used to render `record.history`, a field
 *  the server filled with an empty array on every response, which is how a case
 *  with a year of changes on it said "nothing has happened yet".
 */
export function HistorySection({ caseId }: { caseId: string }) {
  return <CaseHistory caseId={caseId} />
}
